import { useEffect, useState } from 'react';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import usePlanStore from '@/stores/PlanStore';
import useSalesBotStore from '@/stores/SalesBotStore';
import useTeamMemberStore from '@/stores/TeamMemberStore';
import useUserStore from '@/stores/UserStore';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Outlet, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import {
  trackUserAuthenticationFailed,
  trackUserAuthenticationSuccess,
} from '@/lib/analytics-event';
import { isUserAuthenticated, removeToken } from '@/lib/utils';
import { fetchGoals } from '@/hooks/agent';
import {
  fetchCustomerPlans,
  fetchCustomerTags,
  fetchOwnerTags,
} from '@/hooks/inbox';
import { fetchCannedMessages, fetchUserDetails } from '@/hooks/user';
import { fetchWorkflows } from '@/hooks/workflow';

import AblyManager from '../common/AblyManager';
import { AblyMessages } from '../common/AblyMessages';
import ConnectionBanner from '../common/ConnectionBanner';

// Function to check if a route is auth protected ad if the user is authenticated
export default function AuthLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const {
    user,
    setUser,
    setCustomer,
    resetUserStore,
    setCannedResponses,
    setTFAState,
    setTFADefaultMethod,
    setTFAStatus,
    setCopilotEnabled,
    setIgapiEnabled,
    setWorkflowEnabled,
    setPersonalizeOpensEnabled,
    setSalesBotEnabled,
    setAutoPilotFeatureEnabled,
    setRecordingFeatureEnabled,
    setLinkedinFeatureEnabled,
    setEmailFeatureEnabled,
    setIsInstagramStandaloneLoginEnabled,
    setOpensFeatureEnabled,
    setIgOpensAvailable,
    setOpensSessionActive,
    setOpensNeedLogin,
    setSentAudioTranscriptionFeatureEnabled,
    setSelfServiceOpensEnabled,
    setUserCopilotPrompt,
    setUserCopilotAISelection,
    setUserCopilotAIService,
    setNextOnboardingStage,
    setIsSubscriptionActive,
    setConnectedServicesCount,
    setConnectedServices,
    setIsDemoAccount,
  } = useUserStore(
    useShallow((state) => ({
      user: state.user,
      setUser: state.setUser,
      setCustomer: state.setCustomer,
      resetUserStore: state.resetUserStore,
      setCannedResponses: state.setCannedResponses,
      setTFAState: state.setTFAState,
      setTFADefaultMethod: state.setTFADefaultMethod,
      setTFAStatus: state.setTFAStatus,
      setCopilotEnabled: state.setCopilotEnabled,
      setIgapiEnabled: state.setIgapiEnabled,
      setWorkflowEnabled: state.setWorkflowEnabled,
      setPersonalizeOpensEnabled: state.setPersonalizeOpensEnabled,
      setSalesBotEnabled: state.setSalesBotEnabled,
      setAutoPilotFeatureEnabled: state.setAutoPilotFeatureEnabled,
      setRecordingFeatureEnabled: state.setRecordingFeatureEnabled,
      setLinkedinFeatureEnabled: state.setLinkedinFeatureEnabled,
      setEmailFeatureEnabled: state.setEmailFeatureEnabled,
      setIsInstagramStandaloneLoginEnabled:
        state.setIsInstagramStandaloneLoginEnabled,
      setOpensFeatureEnabled: state.setOpensFeatureEnabled,
      setIgOpensAvailable: state.setIgOpensAvailable,
      setOpensSessionActive: state.setOpensSessionActive,
      setOpensNeedLogin: state.setOpensNeedLogin,
      setSentAudioTranscriptionFeatureEnabled:
        state.setSentAudioTranscriptionFeatureEnabled,
      setSelfServiceOpensEnabled: state.setSelfServiceOpensEnabled,
      setUserCopilotPrompt: state.setUserCopilotPrompt,
      setUserCopilotAISelection: state.setUserCopilotAISelection,
      setUserCopilotAIService: state.setUserCopilotAIService,
      setNextOnboardingStage: state.setNextOnboardingStage,
      setIsSubscriptionActive: state.setIsSubscriptionActive,
      setConnectedServicesCount: state.setConnectedServicesCount,
      setConnectedServices: state.setConnectedServices,
      setIsDemoAccount: state.setIsDemoAccount,
    }))
  );

  const {
    setTags,
    setOwners,
    resetInboxStore,
    addLinkedinChannel,
    addSmsChannel,
    addEmailChannel,
  } = useInboxStore(
    useShallow((state) => ({
      setTags: state.setTags,
      setOwners: state.setOwners,
      resetInboxStore: state.resetInboxStore,
      addLinkedinChannel: state.addLinkedinChannel,
      addSmsChannel: state.addSmsChannel,
      addEmailChannel: state.addEmailChannel,
    }))
  );
  const { setAgents, setAgentGoals, setCompanyGoal } = useTeamMemberStore(
    useShallow((state) => ({
      setAgents: state.setAgents,
      setAgentGoals: state.setAgentGoals,
      setCompanyGoal: state.setCompanyGoal,
    }))
  );

  const { setAutoPilotEnabled } = useSalesBotStore(
    useShallow((state) => ({
      setAutoPilotEnabled: state.setAutoPilotEnabled,
    }))
  );

  const resetContactStore = useContactStore((state) => state.resetContactStore);

  const setPlans = usePlanStore((state) => state.setPlans);

  const { setWorkflowList, setWorkflowListLoading } = useWorkflowStore(
    useShallow((state) => ({
      setWorkflowList: state.setWorkflowList,
      setWorkflowListLoading: state.setWorkflowListLoading,
    }))
  );
  useEffect(() => {
    if (!isUserAuthenticated()) {
      navigate('/login', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  // Fetch loggedIn user data
  const fetchUserData = async () => {
    try {
      const response = await fetchUserDetails();
      setUser(response);

      const tfaMethods = response.enabled_methods || [];
      setTFAState('totp', tfaMethods.includes('totp'));
      setTFAState('mobile', tfaMethods.includes('mobile'));
      if (response.customer?.tfa_status) {
        setTFAStatus(response.customer.tfa_status);
      }
      if (response.default_method) {
        setTFADefaultMethod(response.default_method);
      }

      setCustomer(response.customer_id);
      setCopilotEnabled(response.customer.is_copilot_enabled);
      // Set user specific copilot settings
      if (response.ai_selection?.copilot) {
        setUserCopilotPrompt(response.ai_selection.copilot?.prompt);
        setUserCopilotAISelection(response.ai_selection.copilot?.selection);
        setUserCopilotAIService(response.ai_selection.copilot?.service);
      }
      setIgapiEnabled(response.customer.is_igapi_enabled);
      setWorkflowEnabled(response.customer.is_workflow_enabled);
      setPersonalizeOpensEnabled(
        response.customer.is_personalized_opens_enabled
      );
      setSalesBotEnabled(response.customer.is_salesbot_enabled);
      setAutoPilotEnabled(response.customer.is_autopilot_enabled);
      setAutoPilotFeatureEnabled(
        response.customer.is_autopilot_feature_enabled
      );
      setRecordingFeatureEnabled(
        response.customer.is_recording_feature_enabled
      );
      setLinkedinFeatureEnabled(response.customer.is_linkedin_feature_enabled);
      setEmailFeatureEnabled(response.customer.is_email_enabled);
      setIsInstagramStandaloneLoginEnabled(
        response.customer.is_instagram_standalone_login_enabled
      );
      if (response?.customer?.is_linkedin_feature_enabled) {
        addLinkedinChannel();
      }
      if (response?.customer?.is_ghl_sms_enabled) {
        addSmsChannel();
      }
      if (response?.customer?.is_email_enabled) {
        addEmailChannel();
      }
      setOpensFeatureEnabled(
        response.customer.is_automated_opens_feature_enabled
      );
      setOpensSessionActive(response.customer.is_ig_opens_session_active);
      setOpensNeedLogin(response.customer.is_opens_need_login);
      setIgOpensAvailable(response.customer.is_ig_opens_available);
      setSentAudioTranscriptionFeatureEnabled(
        response.customer.is_transcription_for_sent_enabled
      );
      setSelfServiceOpensEnabled(response.customer.self_service_opens_feature);

      // Set additional customer fields
      setNextOnboardingStage(response.customer.next_onboarding_stage);
      setIsSubscriptionActive(response.customer.is_subscription_active);
      setConnectedServicesCount(response.customer.connected_services_count);
      setConnectedServices(response.customer.connected_services || []);

      // Handle onboarding flow based on current and next stages
      const { current_onboarding_stage, next_onboarding_stage } =
        response.customer;

      if (next_onboarding_stage === 'additional_details') {
        navigate('/onboarding/setup', { replace: true });
      } else if (next_onboarding_stage === 'connect_account') {
        navigate('/onboarding/connect-channel', { replace: true });
      } else if (next_onboarding_stage === 'pending_selection') {
        navigate('/onboarding/instagram/select-page', { replace: true });
      } else if (
        current_onboarding_stage === 'onboarded' &&
        next_onboarding_stage === null
      ) {
        // User is fully onboarded, continue with normal flow
      }

      setIsDemoAccount(response.customer.is_demo_account);
      trackUserAuthenticationSuccess(response);
    } catch (error) {
      console.error('Error fetching user details:', error);
      if (error === 'Invalid credentials') {
        removeToken();
        // reset user store
        resetUserStore();
        // reset contact store
        resetContactStore();
        // reset inbox store
        resetInboxStore();

        trackUserAuthenticationFailed(error);

        navigate('/login', { replace: true });
      }
    }
  };

  // Fetch customer tags list
  const fetchTags = async () => {
    try {
      const response = await fetchCustomerTags();
      setTags(response);
    } catch (error) {
      console.error('Error fetching customer tags list:', error);
    }
  };

  // Fetch customer owners list
  const fetchOwners = async () => {
    try {
      const response = await fetchOwnerTags();
      setOwners(response);
      // TODO: There are two instances of owners/agents - one in inboxStore and one in teamMembersStore. Need to remove one of them. Decide and remove
      setAgents(response);
    } catch (error) {
      console.error('Error fetching customer owners list:', error);
    }
  };

  const fetchAgentGoals = async () => {
    try {
      const response = await fetchGoals();
      const { company, agents } = response;
      setAgentGoals(agents);
      company?.goal && setCompanyGoal(company.goal);
    } catch (error) {
      console.error('Error fetching agent goals:', error);
    }
  };

  const fetchUserCannedResponses = async () => {
    try {
      const response = await fetchCannedMessages();
      setCannedResponses(response);
    } catch (error) {
      console.error('Error fetching canned responses:', error);
    }
  };

  // Fetch customer plans list
  const fetchPlans = async () => {
    try {
      const response = await fetchCustomerPlans();
      setPlans(response.plans);
    } catch (error) {
      console.error('Error fetching customer plans list:', error);
    }
  };

  const fetchWorkflowList = async () => {
    try {
      setWorkflowListLoading(true);
      const response = await fetchWorkflows();
      setWorkflowList(response.data);
      setWorkflowListLoading(false);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setWorkflowListLoading(false);
    }
  };

  useEffect(() => {
    if (isUserAuthenticated() && !user) {
      fetchUserData();
      fetchTags();
      fetchOwners();
      fetchPlans();
      fetchAgentGoals();
      fetchUserCannedResponses();
      fetchWorkflowList();
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchTags();
      fetchOwners();
      fetchPlans();
      fetchAgentGoals();
      fetchUserCannedResponses();
      fetchWorkflowList();
    }
  }, []);

  return loading ? null : (
    <>
      <ConnectionBanner />
      <AblyManager />
      <AblyMessages />
      <Outlet />
    </>
  );
}
